import { el } from '@/lib/dom'
import { T } from '@/lib/i18n'
import { IMAGE_WIDTH, mediaUrl, queueImage } from '@/lib/images'
import type { ContentCard } from '@/lib/catalog'
import { toneOf } from '@/lib/catalog'
import { joinToFit, widthMeasurer } from '@/lib/textfit'

/**
 * Kart qurucusu.
 *
 * ⚠ Şəkil BURADA yüklənmir — yalnız `pending` siyahısına yazılır. Yükləmə
 * sırası (images.ts) sıraya fokus düşəndə açılır. Ana ekranda ~170 kart var;
 * hamısının posterini eyni anda istəmək TV-ni bir neçə saniyəlik dondurur.
 */

export interface BuiltCard {
  node: HTMLElement
  /**
   * Kartın arxasındakı kataloq elementi.
   *
   * ⚠ OPSİONALDIR: serial bölüm kartında (`screens/detail.ts` →
   *   `buildEpisodeCard`) belə bir element YOXDUR — bölümün nə slug-ı, nə də
   *   kataloq kartı var. Eyni `loadImage()` müqaviləsindən istifadə etsin deyə
   *   sahə məcburi saxlanılmır (uydurma `ContentCard` düzəltməkdənsə boş qalsın).
   */
  item?: ContentCard
  /** Sıraya fokus düşəndə çağırılır. */
  loadImage(): void
}

/**
 * Bir kataloq kartının DOM-unu qurur (poster · başlıq · nişanlar).
 * Hər kart eyni funksiyadan çıxır ki, ölçü və struktur bütün sıralarda eyni olsun.
 *
 * @param hoverExtra fokus qatına ƏLAVƏ meta sətri — hazırda yalnız «Tezliklə»
 *   ekranı işlədir (premyera tarixi, MYTasks 528). Qurulma anında yazılır,
 *   fokusda DOM-a toxunulmur (sıfır reaktivlik qaydası pozulmur).
 */
export function buildCard(item: ContentCard, progress?: number, hoverExtra?: string, wide = false): BuiltCard {
  const node = el('div', 'card focusable tone-' + toneOf(item.slug || item.title) + (wide ? ' card--wide' : ''))

  /**
   * ── ÖNGÖRÜNÜM QARMAĞI (MYTasks 586) ────────────────────────────────────────
   * Sahib: «item üzərinə focus olanda 1 saniyə sonra trailerin 5-ci saniyəsindən
   * 10-cu saniyəsinə qədər səssiz oynasın — əgər dondurmasa».
   *
   * ⚠ NİYƏ ATRİBUT, `FocusItem.data` DEYİL: `data` sahəsini ekranların YALNIZ
   *   BİRİ (`home.ts`) doldurur — qalan beş ekranda kart eyni qurucudan çıxsa da
   *   fokus elementi boş `data` ilə yazılır. Atribut kartın ÖZÜNDƏDİR, yəni
   *   öngörünüm hər ekranda avtomatik işləyir və heç bir ekran faylına toxunmur.
   * ⚠ Qurulma anında BİR DƏFƏ yazılır (sıfır reaktivlik); fokus addımında yalnız
   *   OXUNUR — atribut oxumaq layout tetiklemir (`getBoundingClientRect` deyil).
   * ⚠ YAŞ HƏDDİ DƏ YAZILIR: 18+ kartın treyleri yaşı çatmayana OYNAMAMALIDIR
   *   (MYTasks 530 qadağası) — saytdakı `data-age-limit` qarmağının eynisi.
   */
  if (item.slug) node.setAttribute('data-preview', item.slug)
  if (item.ageLimit) node.setAttribute('data-age-limit', String(item.ageLimit))

  // Fallback ƏVVƏL gəlir — poster gələnə qədər görünən budur.
  node.appendChild(el('div', 'card__fallback', item.title))

  // ⚠ En VERİLİR: kart 220 px-dir, orijinal poster isə 1000+ px olur. TV-də JPEG
  // dekodlaması əsas axındadır və vaxt piksel sayı ilə mütənasibdir — 320 px-lik
  // variant ~10 dəfə az dekodlama deməkdir (PERFORMANCE.md §4).
  const url = mediaUrl(wide ? item.backdropUrl || item.posterUrl : item.posterUrl, IMAGE_WIDTH.card)
  let image: HTMLImageElement | null = null
  if (url) {
    image = el('img', 'card__img')
    image.alt = ''
    node.appendChild(image)
  }

  // ── Yuxarı nişanlar: yaş SOLDA, reytinq SAĞDA (MYTasks 524 + 525) ────────
  // Sahib: «yaş itemin sol üst küncündə badge ilə görünsün, cari yerdən silinsin»
  // + «rating hazırkı vəziyyətindən çıxsın və sağ üst küncdə badge ilə görünsün
  // item hover zamanı».
  // ⚠ Yaş nişanı ARTIQ DA sol-üstdə idi — dəyişən onun meta sətrindən çıxarılması
  //   və reytinqin ona QOŞULMASIDIR (əvvəl reytinq adın yanında idi).
  if (item.ageLimit) node.appendChild(el('div', 'card__badge', item.ageLimit + '+'))
  if (item.rating) node.appendChild(el('div', 'card__badge card__badge--rating', item.rating.toFixed(1)))

  // «Davam et» sırasının baxılma zolağı (Android TV `TvPosterCard`-ın qarşılığı).
  // ⚠ En INLINE stilə yazılır: hər kartda fərqli faizdir, CSS sinfi ilə verilə
  //   bilməz. Yazı BİR DƏFƏ, qurulma anında olur — sonra kartın DOM-una
  //   toxunulmur (sıfır reaktivlik).
  if (progress !== undefined && progress > 0) {
    const bar = el('div', 'card__progress')
    const fill = el('div', 'card__progress-fill')
    fill.style.width = Math.round(Math.min(1, progress) * 100) + '%'
    bar.appendChild(fill)
    node.appendChild(bar)
  }

  // ── Fokus qatı — ANDROID TV `TvPosterCard` İLƏ EYNİ MAKET ────────────────
  // Sahib tələbi (2026-08-07): «androiddə olan TV yanaşması funksionallığını
  // veb TV-də və iOS TV-də də et». Android TV-də fokuslanan kartın afişası
  // ALTDAN 38% qara zolaqla örtülür: ad (iki sətrə keçir) + sağında reytinq,
  // altında meta. Veb TV-də bu qat ÜMUMİYYƏTLƏ yox idi — ad yalnız posteri
  // olmayan kartda (`card__fallback`) görünürdü.
  //
  // ⚠ DOM BİR DƏFƏ QURULUR, fokusda YENİLƏNMİR (PERFORMANCE.md §1 «sıfır
  //   reaktivlik»): mətn burada yazılır, görünmə isə `.card.is-focused`
  //   seçicisi ilə YALNIZ `opacity`-dən keçir — fokus addımında layout yoxdur.
  // ⚠ Fade YOXDUR (Android TV-də də yoxdur): pultla sürətlə gəzəndə hər kartda
  //   0,5 saniyəlik keçid interfeysi «yağlı» göstərir. Saytdakı yavaş fade
  //   SİÇAN üçündür — orada istifadəçi bir kartın üstündə dayanır.
  const hover = el('div', 'card__hover')
  const head = el('div', 'card__hover-head')
  head.appendChild(el('div', 'card__hover-title', item.title))
  // ⚠ REYTİNQ ARTIQ BURADA DEYİL (MYTasks 525) — o, afişanın SAĞ-ÜST nişanıdır.
  //   Nəticə ad üçün faydalıdır: sətir indi qatın tam enini tutur.
  hover.appendChild(head)
  // ── İki meta sətri — saytdakı `.card__hover-line` cütünün TV qarşılığı ────
  // Sahib tələbi (MYTasks 512): «hoverdə filmin adı altında ili, vaxtı, janrı,
  // moodu, age ratesi və ratingi görünsün». Reytinq adın sağındadır, qalanı
  // burada:
  //     1-ci sətir — il · müddət · yaş
  //     2-ci sətir — janr · əhval
  //
  // ⚠ ƏVVƏL YALNIZ İL VAR İDİ və yaş qəsdən yazılmırdı («sol-üst küncdə
  //   `card__badge` var, iki yerdə təkrarlamayaq»). Sahib yaşı meta sətrində
  //   açıq istədi — nişan yerində qalır, sətirdəki isə onun təkrarı deyil,
  //   sətrin tam oxunması üçündür (3 metrdən künc nişanı gözdən qaçır).
  // ⚠ MƏLUMAT KART CAVABINDADIR (catalog.ts qeydinə bax) — fokus addımında
  //   nə sorğu, nə DOM yenilənməsi var: mətn qurulma anında yazılır.
  const primary = primaryMeta(item)
  if (primary) hover.appendChild(el('div', 'card__hover-meta', primary))
  const taxonomy = taxonomyMeta(item)
  if (taxonomy) hover.appendChild(el('div', 'card__hover-meta', taxonomy))
  // Əlavə sətir (məs. premyera tarixi) — çağıran hazır mətn verir; büdcə/qısaltma
  // qərarı da onundur (`screens/upcoming.ts`-dəki qeydə bax).
  if (hoverExtra) hover.appendChild(el('div', 'card__hover-meta', hoverExtra))
  node.appendChild(hover)

  let requested = false
  return {
    node,
    item,
    loadImage() {
      if (requested || !image || !url) return
      requested = true
      queueImage(image, url)
    },
  }
}

/**
 * Fokus qatının JANR sətrində göstərilən maksimum janr sayı.
 *
 * ⚠ RƏQƏM ÖLÇÜLÜB. Sətrin daxili eni 204 px-dir (220 px kart − 2×8 px padding),
 *   şrift `--text-xs` = 20 px. Brauzerdə ölçüldü (Chrome, `--font-sans`,
 *   preview build): ən uzun iki janr «Bioqrafiya · Fantastika» = 199 px → 1
 *   sətir; üstünə ən uzun əhval gələndə («… · Ürəyəyatımlı») 326 px = 2 sətir
 *   — qat bunu udur. Üçüncü janr isə sətri 307 px-ə (əhvalsız) çıxarır, yəni
 *   aşağıdakı en büdcəsi əhvalı onsuz da atardı. Saytda da hədd İKİDİR
 *   (`CardHoverInfo.tsx` → `genres.slice(0, 2)`).
 */
const MAX_GENRES = 2

/**
 * Janr/əhval sətrinin EN büdcəsi — sığmayan hissə ümumiyyətlə yazılmır.
 *
 * ⚠ 204 px = 220 px kart − 2×8 px padding (`tv.css` → `--card-width` və
 *   `.card__hover` daxili boşluğu). İki sətir — CSS-dəki `-webkit-line-clamp: 2`
 *   ilə eyni rəqəm; klamp indi yalnız sığortadır, qərarı kod verir.
 *   YOXLANILDI (yığılmış app, fokuslanmış kart): `.card__hover-meta`
 *   `clientWidth` = 204, `clientHeight` = 48 (2 × 24 px sətir) — yəni bu iki
 *   rəqəm CSS-dəki faktiki qutu ilə üst-üstə düşür.
 * ⚠ ƏVVƏL BU BÜDCƏ SİMVOL SAYI İDİ (`MAX_META_CHARS = 38`) və KİRİLDƏ İŞLƏMİRDİ:
 *   38 latın simvolu 326,0 px, 38 kiril simvolu 397,5 px yer tutur (+22%) —
 *   «Драма · Комедия · Поднимает настроение» düz 38 simvoldur və üç sətir
 *   tutur. Ölçüldü (real adların bütün birləşmələri, Chrome headless, 204 px):
 *       əvvəl → az 0/1836 · en 29/1683 (1,7%) · **ru 482/1683 (28,6%) KƏSİLMƏ**
 *       sonra → az 0/1836 · en 0/1683      · **ru 0/1683**
 *   Yəni kod öz vədini («kəsilmiş mətndənsə olmayan mətn») indi yerinə yetirir.
 *   Protokol: `scripts/measure-meta.mjs` (`pnpm --filter @kinobox/tv measure:meta`).
 * ⚠ QİYMƏT RU-DA MƏLUMATLADIR, KƏSİLMƏ İLƏ YOX: üç hissənin hamısı ru-da
 *   1683-dən 848-ində (50,4%) qalır — qalanında ƏHVAL atılır. Əvvəlki kod 79%
 *   hallarda üç hissəni yazırdı, amma onların dörddə biri ekranda yarımçıq
 *   görünürdü. Az/en-də fərq praktiki olaraq yoxdur (en: 1643 → 1646).
 */
const META_WIDTH = 204
const META_LINES = 2

/**
 * Meta sətrinin ölçəni — `--text-xs` ölçüsündə, adi çəkidə (bax `textfit.ts`).
 *
 * ⚠ MODUL SƏVİYYƏSİNDƏDİR ki, ölçmə keşi BÜTÜN kartlar üçün ortaq olsun: ana
 *   ekranda ~170 kart var, janr/əhval adları isə cəmi 30-dur — hər ad bir dəfə
 *   ölçülür, qalan kartlar hazır rəqəmi götürür. ÖLÇÜLDÜ (121 kartlıq ana
 *   ekran): boot-da 33 `measureText` (cəmi 0,4 ms) + 1 `getComputedStyle`;
 *   11 fokus addımından sonra rəqəmlər DƏYİŞMİR — fokusda ölçmə yoxdur.
 */
const measureMeta = widthMeasurer('--text-xs')

/**
 * «1s 40 dəq» — müddətin QISA forması (saytın `formatRuntime(short)` əkizi).
 *
 * ⚠ SAAT BAŞ HƏRFLƏDİR (sahib tələbi 2026-08-06, MYTasks 482: «item hoverdə
 *   saat yox s yazılsın, hour yox h»). Detal ekranında (`screens/detail.ts`)
 *   tam söz qalır — orada sətir bütün ekran enindədir, kartda isə 204 px.
 *   Ölçüldü: «2024 · 1s 40 dəq · 16+» = 201 px, yəni tam söz («1 saat 40 dəq»)
 *   ilə sətir mütləq ikiyə bölünərdi.
 * ⚠ TV paketi saytın `formatRuntime`-ını IMPORT EDƏ BİLMİR (ayrı build hədəfi,
 *   ES2015 API həddi) — qayda üç sətirdə təkrarlanır, asılılıq gətirilmir.
 */
function shortRuntime(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours === 0) return rest + T(' dəq')
  if (rest === 0) return hours + T('s')
  return hours + T('s') + ' ' + rest + T(' dəq')
}

/**
 * «1 saat 42 dəq» — müddətin TAM forması (Android `formatRuntime(short = false)`
 * əkizi; film modalının meta sətri məhz bu formanı işlədir — MYTasks 527 qısa
 * forma YALNIZ kart fokus qatı üçündür).
 *
 * ⚠ `screens/detail.ts` eyni sətri hələ öz içində yığır (o ekran dərin keçid /
 *   ehtiyat yoludur, davranışına toxunulmur) — açarlar (` saat` · ` saat ` ·
 *   ` dəq`) hər iki yerdə eynidir.
 */
export function fullRuntime(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours === 0) return rest + T(' dəq')
  if (rest === 0) return hours + T(' saat')
  return hours + T(' saat ') + rest + T(' dəq')
}

/**
 * Növ etiketi — Android `ContentKind.label()` əkizi. Naməlum növdə boş sətir —
 * uydurma söz yazılmır (hero-da boş etiket qutusu qalmasın).
 *
 * ⚠⚠ DÖRD ETİKETİN TƏK YERİ (2026-08-30). `browser/screens/home.ts`-də BAYT-BAYT
 *    eyni nüsxə vardı və İŞLƏNƏN məhz o idi (`browser/shell.ts` onu import edirdi),
 *    buradakı isə ölü qalmışdı. İki nüsxə = tərcümə bir yerdə düzəlib o birində
 *    qalma riski (məsələn «Tamaşa» → «Teatr» yazılışı); nüsxə silindi, hər iki
 *    hədəf bu funksiyanı çağırır.
 * ⚠ BRAUZER BUNDLE-INI AĞIRLAŞDIRMIR: `browser/screens/home.ts` bu fayldan onsuz
 *   da `heroSummaryParts`-ı import edir, yəni modul hər iki bundle-da idi.
 */
export function kindLabel(kind: string | null | undefined): string {
  if (kind === 'movie') return T('Film')
  if (kind === 'series') return T('Serial')
  if (kind === 'theatre') return T('Tamaşa')
  if (kind === 'concert') return T('Konsert')
  return ''
}

/** Fokus qatının 1-ci sətri — «2024 · 1s 40 dəq» (yaş nişandadır). */
export function primaryMeta(item: ContentCard): string {
  const parts: string[] = []
  if (item.year) parts.push(String(item.year))
  // Müddət SERİALDA boşdur (server qeydi) — sətir sadəcə «2023 · 16+» olur.
  if (item.durationMinutes) parts.push(shortRuntime(item.durationMinutes))
  // ⚠ YAŞ BURADA YAZILMIR (MYTasks 524) — o, afişanın SOL-ÜST nişanındadır.
  return parts.join(' · ')
}

/**
 * Fokus qatının 2-ci sətri — «Dram · Komediya · Nostalji».
 *
 * ⚠ SIRA ƏHƏMİYYƏTLİDİR: hissələr bu ardıcıllıqla sınanır və sığmayan ATILIR,
 *   yəni ilk atılan həmişə SONUNCU hissə (əhval) olur — janrlar daha vacibdir.
 */
export function taxonomyMeta(item: ContentCard): string {
  const parts: string[] = []
  const genres = item.genreNames
  if (genres) {
    for (let i = 0; i < genres.length && parts.length < MAX_GENRES; i++) {
      const name = genres[i]
      if (name) parts.push(name)
    }
  }
  // ⚠ ƏHVAL YALNIZ BİRDİR — saytdakı ilə eyni (`slice(0, 1)`). İkinci əhval
  //   sətri hər halda üçüncü sətrə keçirir və heç bir yeni məlumat vermir.
  const moods = item.moodNames
  if (moods && moods[0]) parts.push(moods[0])
  return joinToFit(parts, ' · ', META_WIDTH, META_LINES, measureMeta)
}

// ⚠ `metaLine` («2024 · 16+» çip sətri) SİLİNDİ (522): yeganə istifadəçisi hero
//   idi — orada meta artıq başlığın üstündəki «FİLM · 2016 · 16+» sətridir
//   (`kindLabel` + il + yaş, `screens/home.ts` → `buildHero`).

/**
 * HERO TƏSVİRİNİN KƏSİLMƏ HƏDDİ — «… daha çox» nişanı buna görə qoyulur.
 *
 * ⚠⚠ NİYƏ SİMVOL SAYIDIR, ÖLÇÜLMÜŞ HÜNDÜRLÜK DEYİL (sahib tələbi 2026-08-11:
 *   «herodakı məlumatlar tam sığmadığına görə üç nöqtədən sonra daha çox altdan
 *   xətt çəkilmiş halda olsun ki user bilsin ki tam deyil»). Faktiki daşmanı
 *   ölçmək layout oxumaq deməkdir — bu app-da qadağandır (PERFORMANCE.md: fokus
 *   arifmetikadır, `getBoundingClientRect` yoxdur). Sabit büdcə dörd TV səthində
 *   eyni nəticə verir və heç nə ölçmür.
 * ⚠ Android `HERO_SUMMARY_LIMIT` və iOS `heroSummaryLimit` ilə EYNİ rəqəm.
 */
export const HERO_SUMMARY_LIMIT = 155

/** Hero təsvirini söz sərhədində kəsir: [göstəriləcək mətn, kəsildimi]. */
export function heroSummaryParts(text: string): [string, boolean] {
  const clean = text.replace(/^\s+|\s+$/g, '')
  if (clean.length <= HERO_SUMMARY_LIMIT) return [clean, false]
  const cut = clean.slice(0, HERO_SUMMARY_LIMIT)
  const lastSpace = cut.lastIndexOf(' ')
  const body = lastSpace > HERO_SUMMARY_LIMIT / 2 ? cut.slice(0, lastSpace) : cut
  return [body.replace(/[\s,;:.\u2014-]+$/g, ''), true]
}
